/**
 * A/B Test Configuration
 * Defines variant configurations for homepage hero section
 */

export type HeadlineSegment = string | { text: string; hasDash?: boolean; className?: string };

export interface VariantConfig {
	id: string;
	name: string;
	hero: {
		headline: {
			// Mobile: Array where each element is either:
			// - A string (simple text line)
			// - An array of HeadlineSegments (for complex formatting within a line)
			mobile: (HeadlineSegment | HeadlineSegment[])[];
			// Desktop: Array of segments (strings or formatted objects)
			desktop: HeadlineSegment[];
		};
		description: string;
	};
}

/**
 * A/B Test Variants Configuration
 * Add new variants here as needed
 */
export const AB_TEST_VARIANTS: Record<string, VariantConfig> = {
	A: {
		id: "A",
		name: "Variant A",
		hero: {
			headline: {
				mobile: [
					"It's time the world",
					["understood your ", { text: "career", hasDash: true }, " as"],
					"well as you do.",
				],
				desktop: [
					"It's time the world understood your ",
					{ text: "career", hasDash: true },
					" as well as you do.",
				],
			},
			description:
				"Meet Profound - your private AI career concierge. It listens. It understands your journey. It connects you with the right people, in the right rooms, at the right time.",
		},
	},
	B: {
		id: "B",
		name: "Variant B",
		hero: {
			headline: {
				mobile: [
					"Transform your",
					["career ", { text: "journey", hasDash: true }],
					"with AI-powered insights.",
				],
				desktop: [
					"Transform your career ",
					{ text: "journey", hasDash: true },
					" with AI-powered insights.",
				],
			},
			description:
				"Profound is your personal career advisor. Get intelligent guidance, connect with opportunities, and advance your professional path.",
		},
	},
	// Add more variants as needed
	// Example with multiple formatted words:
	// C: {
	//   id: "C",
	//   name: "Variant C",
	//   hero: {
	//     headline: {
	//       mobile: [
	//         "Line 1",
	//         ["Text with ", { text: "formatted", hasDash: true }, " word"],
	//         "Line 3"
	//       ],
	//       desktop: [
	//         "Full text with ",
	//         { text: "formatted", hasDash: true },
	//         " word and more text."
	//       ]
	//     },
	//     description: "Description text"
	//   }
	// }
};

/**
 * Check if a variant ID is valid (exists in configuration)
 * @param variant - Variant ID to validate
 * @returns true if variant exists in config, false otherwise
 */
export function isValidVariant(variant: string): boolean {
	return variant in AB_TEST_VARIANTS;
}

/**
 * Get variant configuration by ID
 * @param variant - Variant ID
 * @returns VariantConfig if found, null otherwise
 */
export function getVariantConfig(variant: string): VariantConfig | null {
	return AB_TEST_VARIANTS[variant] || null;
}

/**
 * Get all available variant IDs
 * @returns Array of variant IDs
 */
export function getAvailableVariants(): string[] {
	return Object.keys(AB_TEST_VARIANTS);
}

